import React, { useCallback, useState, useEffect, useRef } from 'react';
import _ from 'lodash';

import { getQueryParams } from 'utils/url';


export const sizeDown = size => {
  switch(size) {
    case 'xxl': return 'xl'
    case 'xl': return 'l'
    case 'l': return 'm'
    case 'm': return 's'
    case 's': return 'xs'
    case 'xs': return 'xs'
  }
}

export function useDebounceValue(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

export const useDidMount = () => {
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
  }, []);
  return isMounted;
};

export const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  });

  useEffect(() => {
    function tick() {
      savedCallback.current();
    }

    let id = setInterval(tick, delay);
    return () => clearInterval(id);
  }, [delay]);
}

export const useTimeout = (delay, callback) => {
  const savedCallback = useRef(callback);
  const timeoutId = useRef(null);

  // Update saved callback when callback changes
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const start = useCallback(() => {
    // Clear any existing timeout
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    // Don't schedule if no delay is specified or no callback
    if (delay === null || delay === undefined || !callback) {
      return;
    }

    function tick() {
      savedCallback.current?.();
    }

    timeoutId.current = setTimeout(tick, delay);
  }, [delay, callback]);

  const cancel = useCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  return { start, cancel };
};

export const usePolling = (callback, delay) => {
  const savedCallback = useRef();
  const timerId = useRef();
  
  const tick = () => {
    savedCallback.current();
  }

  useEffect(() => {
    savedCallback.current = callback;
  });

  useEffect(() => {
    timerId.current = setInterval(tick, delay);
    return () => { 
      clearInterval(timerId.current);
      timerId.current = null;
    };
  }, [delay]);

  const resetPolling = () => {
    clearInterval(timerId.current);
    timerId.current = setInterval(tick, delay);
  }

  const stopPolling = () => {
    clearInterval(timerId.current);
    timerId.current = null;
  }
  
  const isPolling = Boolean(timerId.current);

  return [ isPolling, stopPolling, resetPolling ]
}


export const useUpdateEffect = (effect, dependencies) => {
  const isMounted = useDidMount();
  const isInitialMount = useRef(true);
  
  useEffect(() => {
    let effectCleanupFunc = function noop() {};

    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      effectCleanupFunc = effect() || effectCleanupFunc;
    }
    return () => {
      effectCleanupFunc();
      if (!isMounted.current) {
        isInitialMount.current = true;
      }
    };
  }, dependencies);
};



export const withPolling = Component => {
  return class extends React.Component {
    state = {
      timers: []
    }

    startPolling = async (fn, length, now = false) => {
      let timers = this.state.timers;

      // Remove if this already exists
      let oldTimer = _.find(timers, { fn });
      if(oldTimer) {
        clearInterval(oldTimer.id);
        timers = _.reject(timers, { id: oldTimer.id });
      }

      // Start and add to list
      let id = setInterval(fn, length)
      this.setState({
        timers: _.concat(timers, { id, fn, length })
      });
      if(now) await fn();
      return id;
    }

    resetAllPolling = async () => {
      _.forEach(this.state.timers, t =>{
        this.startPolling(t.fn, t.length);
      })
    }

    resetPolling = async (fn, now) => {
      let oldTimer = _.find(this.state.timers, { fn });
      if(oldTimer) {
        return this.startPolling(oldTimer.fn, oldTimer.length, now);
      }
    }

    stopPolling(idOrFn) {
      let timers = this.state.timers;
      if(_.isFunction(idOrFn)) {
        let oldTimer = _.find(timers, { fn: idOrFn });
        if(oldTimer) {
          clearInterval(oldTimer.id);
          this.setState({ timers: _.reject(timers, { id: oldTimer.id }) });
        }
        return;
      }

      this.setState({ timers: _.reject(timers, { id }) });
      clearInterval(id);
    }

    stopAllPolling() {
      let { timers } = this.state;
      _.forEach(timers, t => clearTimeout(t.id));
      this.setState({ timers: [] });
    }

    render() {
      return (
        <Component
          startPolling={this.startPolling.bind(this)}
          stopPolling={this.stopPolling.bind(this)}
          stopAllPolling={this.stopAllPolling.bind(this)}
          resetPolling={this.resetPolling.bind(this)}
          resetAllPolling={this.resetAllPolling.bind(this)}
          {...this.props} />)
    }
  }
}







